import { useState, useRef, useMemo, useCallback } from "react"


import usePagedData from "hooks/usePagedData"
import TextBuildingBlock, { TextBuildingBlockSearchScope, ICreateTextBuildingBlockDto } from "models/TextBuildingBlock"
import UserService from "services/UserService"
import toastMessages from "stores/toastMessages"
import { IApiLink } from "@/types"

interface IUseTextBuildingBlocksModalParams {
    notFormattedOnly?: boolean
    onSelectionInsert?(text: string): void
    onClose(): void
}

export default function useTextBuildingBlocksModal({
    notFormattedOnly,
    onSelectionInsert,
    onClose,
} : IUseTextBuildingBlocksModalParams) {
    const [search, setSearch] = useState<string>()
    const [scope, setScope] = useState<TextBuildingBlockSearchScope>(TextBuildingBlockSearchScope.text)
    const [isAddingTextFormatting, setIsAddingTextFormatting] = useState<boolean | null>(null)
    const scrollEl = useRef<HTMLDivElement>(null)
    const [selectedBlocks, setSelectedBlocks] = useState<TextBuildingBlock[]>([])
    const [isFiltered, setIsFiltered] = useState(false)

    const textBuildingBlocksParams = useMemo(() => {
        setIsFiltered(!!search)
        return {
            search,
            scope,
            pageSize: 30,
            notFormattedOnly,
        }
    }, [search, scope, notFormattedOnly])

    const textBuildingBlocksCall = useCallback(() => {
        return UserService.getTextBuildingBlocks(textBuildingBlocksParams)
    }, [textBuildingBlocksParams])

    const {
        items: textBuildingBlocks,
        refetchData: refetchTextBuildingBlocks,
        loadNextPage,
        isInitialLoad,
        isLoading,
        hasMore,
        aggregate,
    } = usePagedData<TextBuildingBlock, { linkCreate?: IApiLink }>(textBuildingBlocksCall, toastMessages.showSystemError)
    const linkCreate = aggregate?.linkCreate

    function handleFilterChange(filterScope: TextBuildingBlockSearchScope, filterSearch: string) {
        setScope(filterScope)
        setSearch(filterSearch)
    }

    function handleBlockSelectToggle(block: TextBuildingBlock) {
        if (selectedBlocks.some(b => b.id === block.id)) {
            setSelectedBlocks(selectedBlocks.filter(b => b.id !== block.id))
            return
        }
        setSelectedBlocks([...selectedBlocks, block])
    }

    function handleSelectionInsert() {
        onSelectionInsert && onSelectionInsert(selectedBlocks.map(b => b.text).join("\n"))
        onClose()
    }

    function handleAddClick(textFormating: boolean) {
        setIsAddingTextFormatting(textFormating)
    }

    function handleAddCancel() {
        setIsAddingTextFormatting(null)
    }

    const handleAdd = useMemo(() => {
        if (!linkCreate) return

        return async function(createTextBuildingBlockDto: ICreateTextBuildingBlockDto) {
            await UserService.createTextBuildingBlock(linkCreate, createTextBuildingBlockDto)
            await refetchTextBuildingBlocks()
            setIsAddingTextFormatting(null)
        }
    }, [linkCreate, refetchTextBuildingBlocks])

    async function handleUpdate(updateLink: IApiLink, updateTextBuildingBlockDto: ICreateTextBuildingBlockDto) {
        await UserService.createTextBuildingBlock(updateLink, updateTextBuildingBlockDto)
        await refetchTextBuildingBlocks()
    }

    async function handleDelete(deleteLink: IApiLink) {
        await UserService.deleteTextBuildingBlock(deleteLink)
        await refetchTextBuildingBlocks()
    }


    return {
        isAddingTextFormatting,
        isLoading,
        handleAddClick,
        selectedBlocks,
        handleSelectionInsert,
        handleFilterChange,
        textBuildingBlocks,
        scrollEl,
        loadNextPage,
        isInitialLoad,
        hasMore,
        handleAddCancel,
        handleAdd,
        handleUpdate,
        handleDelete,
        handleBlockSelectToggle,
        isFiltered,
    }
}
